import type { ReactNode } from 'react'
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react'

export type PageContentWidth = 'sm' | 'md' | 'lg' | null

interface PageContentWidthContextType {
    pageContentWidth: PageContentWidth
    setPageContentWidth: (width: PageContentWidth) => void
}

// Justified exception: Context export (not a component) - React Refresh limitation
// eslint-disable-next-line react-refresh/only-export-components -- Justified: context export
export const PageContentWidthContext = createContext<PageContentWidthContextType | undefined>(
    undefined
)

interface PageContentWidthProviderProps {
    children: ReactNode
}

export const PageContentWidthProvider: React.FC<PageContentWidthProviderProps> = ({ children }) => {
    const [pageContentWidth, setPageContentWidthState] = useState<PageContentWidth>(null)

    const setPageContentWidth = useCallback((width: PageContentWidth) => {
        setPageContentWidthState(width)
    }, [])

    const value = useMemo<PageContentWidthContextType>(
        () => ({
            pageContentWidth,
            setPageContentWidth,
        }),
        [pageContentWidth, setPageContentWidth]
    )

    return (
        <PageContentWidthContext.Provider value={value}>
            {children}
        </PageContentWidthContext.Provider>
    )
}

// Justified exception: Custom hook export (not a component) - React Refresh limitation
// eslint-disable-next-line react-refresh/only-export-components -- Justified: custom hook export
export const usePageContentWidth = (): PageContentWidthContextType => {
    const context = useContext(PageContentWidthContext)
    if (context === undefined) {
        throw new Error('usePageContentWidth must be used within a PageContentWidthProvider')
    }
    return context
}
